import React, {useState, useEffect} from "react";
import moment from "moment";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {GetLikesService} from "../../services/contractorServices";
import CustomUserName from "../../CustomComponents/CustomUserName";
import {fDateTime} from "../../utils/format-time";
import {Color} from "antd/es/color-picker";
import CustomUserProfile from "../../CustomComponents/CustomUserProfile";
import Box from "@mui/material/Box";

const MyLikes = () => {
	const [loading, setLoading] = useState(true);
	const [likesData, setLikesData] = useState([]);
	const [filterType, setFilterType] = useState("all"); // all, ratings, comments
	const [searchTerm, setSearchTerm] = useState("");

	// Fetch likes data from API
	const fetchLikesData = async () => {
		try {
			setLoading(true);
			const response = await GetLikesService();

			if (response && response.status === true && Array.isArray(response.data)) {
				setLikesData(response.data);
			} else {
				setLikesData([]);
			}
		} catch (error) {
			console.error("Error fetching likes data:", error);
			setLikesData([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchLikesData();
	}, []);

	const handleFilterTypeChange = (newType) => {
		setFilterType(newType);
	};

	const renderStars = (rating) => {
		return [...Array(5)].map((_, index) => (
			<span key={index} className={`star ${index < rating ? "filled" : ""}`}>
				★
			</span>
		));
	};

	const getRatingColor = (rating) => {
		if (rating >= 4) return "rating-excellent";
		if (rating >= 3) return "rating-good";
		if (rating >= 2) return "rating-fair";
		return "rating-poor";
	};

	// Filter data based on search and filters
	const filteredData = likesData.filter((item) => {
		const matchesType = filterType === "all" || (filterType === "ratings" && item.rating_id !== null) || (filterType === "comments" && item.street_id !== null);

		const address = item.rating?.address?.full_address || item.street_comment?.addresses || "";
		const description = item.rating ? `Rating: ${item.rating.stars} stars` : item.street_comment?.description || "";
		const userName = `${item.user.first_name} ${item.user.last_name}`;

		const matchesSearch =
			searchTerm === "" ||
			address.toLowerCase().includes(searchTerm.toLowerCase()) ||
			description.toLowerCase().includes(searchTerm.toLowerCase()) ||
			userName.toLowerCase().includes(searchTerm.toLowerCase());

		return matchesType && matchesSearch;
	});

	// Calculate stats
	const totalLikes = likesData.length;
	const totalRatingsLiked = likesData.filter((item) => item.rating_id !== null).length;
	const totalCommentsLiked = likesData.filter((item) => item.street_id !== null).length;

	const getTypeBadge = (type) => {
		switch (type) {
			case true:
				return <span className="badge badge-success">📍 Zipcode</span>;
			default:
				return <span className="badge badge-warning">🏘️ Street</span>;
		}
	};

	return (
		<div className="dashboard-page">
			<div className="page-header">
				<h2>My Likes</h2>
				<p>View and manage all your liked ratings and comments ({totalLikes} total)</p>
			</div>

			{/* Stats Cards */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-icon stat-icon-primary">
						<div className="bi-hand-thumbs-up" style={{fontSize: 22}} />
					</div>
					<div className="stat-content">
						<div className="stat-number">{totalLikes}</div>
						<div className="stat-label">Total Likes</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-icon stat-icon-success">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" />
						</svg>
					</div>
					<div className="stat-content">
						<div className="stat-number">{totalRatingsLiked}</div>
						<div className="stat-label">Ratings Liked</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-icon stat-icon-info">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
							<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" />
						</svg>
					</div>
					<div className="stat-content">
						<div className="stat-number">{totalCommentsLiked}</div>
						<div className="stat-label">Comments Liked</div>
					</div>
				</div>
			</div>

			{/* Filters and Search */}
			<div className="ratings-controls">
				<div className="filter-section">
					<select className="filter-select" value={filterType} onChange={(e) => handleFilterTypeChange(e.target.value)}>
						<option value="all">All Types</option>
						<option value="ratings">Ratings Only</option>
						<option value="comments">Comments Only</option>
					</select>
				</div>
			</div>

			{/* Likes List */}
			{loading ? (
				<div className="ratings-container">
					<CustomLoader />
				</div>
			) : (
				<div className="ratings-container">
					{filteredData.length === 0 ? (
						<div className="no-ratings">
							<div className="placeholder-icon">
								<svg width="80" height="80" viewBox="0 0 24 24" fill="none">
									<path
										d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.5783 8.50903 2.9987 7.05 2.9987C5.59096 2.9987 4.19169 3.5783 3.16 4.61C2.1283 5.6417 1.5487 7.04097 1.5487 8.5C1.5487 9.95903 2.1283 11.3583 3.16 12.39L12 21.23L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
										stroke="currentColor"
										strokeWidth="1.5"
									/>
								</svg>
							</div>
							<h3>No liked items found</h3>
							<p>{searchTerm ? "Try adjusting your search criteria" : "You haven't liked any ratings or comments yet"}</p>
						</div>
					) : (
						<div className="ratings-grid">
							{filteredData.map((item) => (
								<div key={item.like_id} className="rating-card">
									<div className="rating-card-header">
										<div className="address-info">
											<h4 className={item.rating_id ? "job-address" : "comment-address"}>{item.rating?.address?.full_address || item.street_comment?.addresses}</h4>
											<div className="rating-info">
												<div className="item-type-badge">
													{item.rating_id ? (
														<>
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" />
															</svg>
															<span>Rating</span>
														</>
													) : (
														<>
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" />
															</svg>
															<span>Comment</span>
														</>
													)}
												</div>
												{item?.street_comment && getTypeBadge(item?.street_comment?.comment_type)}
												{/* <span className="comment-date">{moment(item.createdAt).format("MMM DD, YYYY")}</span> */}
											</div>
										</div>
									</div>

									<div className="rating-card-body">
										{item.rating_id && (
											<div className="rating-display">
												<div className={`rating-stars ${getRatingColor(item.rating.stars)}`}>
													{renderStars(item.rating.stars)}
													<span className="rating-number">({item.rating.stars}/5)</span>
												</div>
												{item.rating.job_issues && item.rating.job_issues.length > 0 && (
													<div className="issues-list">
														{item.rating.job_issues.slice(0, 2).map((issue, index) => (
															<span key={index} className="issue-tag">
																{issue.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
															</span>
														))}
														{item.rating.job_issues.length > 2 && <span className="issue-tag more">+{item.rating.job_issues.length - 2} more</span>}
													</div>
												)}
											</div>
										)}

										<div className="meta-item">
											<svg width="15" height="15" viewBox="0 0 24 24" fill="none">
												<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
												<polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
											</svg>
											<span>{fDateTime(item.createdAt)}</span>
										</div>

										{item.street_comment?.description && (
											<div className="comment-description">
												<p>{item.street_comment?.description.length > 120 ? `${item.street_comment?.description.substring(0, 120)}...` : item.street_comment?.description}</p>
											</div>
										)}
									</div>

									<div className="rating-card-footer ">
										{/* <div className="footer-left ">
											<div className="meta-item">
												<svg width="15" height="15" viewBox="0 0 24 24" fill="none">
													<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
													<polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
												</svg>
												<span>{fDateTime(item.createdAt)}</span>
											</div>
										</div> */}
										<div className="footer-right">
											<div className="meta-item more back-btn justify-content-end " style={{fontSize: 13, color: "#000"}}>
												<div className="bi-hand-thumbs-up" style={{fontSize: 16}} />
												By,
												<Box sx={{display: "flex", alignItems: "center", gap: 1}}>
													<CustomUserProfile user={item?.user} width={32} sx={{fontSize: 13}} code />
													<CustomUserName user={item?.user} code sx={{fontSize: 13, color: "#000", fontWeight: 600}} />
												</Box>
												{/* <CustomUserName user={item?.user} code={item.rating_id ? true : false} sx={{fontSize: 13, color: "#000", fontWeight: 600}} /> */}
											</div>
										</div>
										{/*<div className="item-meta">
											 <div className="meta-item">
												<svg width="15" height="15" viewBox="0 0 24 24" fill="none">
													<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
													<polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
												</svg>
												<span>{fDateTime(item.createdAt)}</span>
											</div> 

											<div className="meta-item more back-btn justify-content-end " style={{fontSize: 13, color: "#000"}}>
												<div className="bi-hand-thumbs-up" style={{fontSize: 16}} />
												By,
												<CustomUserName user={item?.user} code={item.rating_id ? true : false} sx={{fontSize: 13, color: "#000", fontWeight: 600}} />
											</div>
										</div>*/}
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default MyLikes;
